import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { addTeacherAvailability, getTeacherAvailability } from "../../../../store/actions/availability";
import { getTeacherData } from "../../../../store/actions/teacher";
import { format, addDays, subDays, isSameDay, startOfWeek, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, endOfMonth, isBefore, startOfDay } from "date-fns";
import moment from "moment";
import styles from "./gridStyle.module.css";
import AddEventModal from "../Calendar/AddEventModal";
import EditEventModal from "../Calendar/EditEventModal";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TIME_SLOTS = [];
for (let hour = 0; hour < 24; hour++) {
    for (let min of ["00", "30"]) {
        const h12 = hour % 12 || 12;
        const ampm = hour < 12 ? "AM" : "PM";
        TIME_SLOTS.push({
            id: `${hour.toString().padStart(2, "0")}:${min}`,
            label: `${h12}:${min} ${ampm}`
        });
    }
}

const AvailabilityGrid = () => {
    const dispatch = useDispatch();
    const [availability, setAvailability] = useState([]);
    const [teacherData, setTeacherData] = useState({});
    const [duration, setDuration] = useState(60);
    const [addEventModal, setAddEventModal] = useState(false);
    const [editEventModal, setEditEventModal] = useState(false);
    const [pendingSlots, setPendingSlots] = useState([]);
    const [viewMode, setViewMode] = useState("week");
    const [baseDate, setBaseDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedSlot, setSelectedSlot] = useState({});
    const [hoveredIdx, setHoveredIdx] = useState(null);

    const fetchAvailability = useCallback(async () => {
        const res = await dispatch(getTeacherAvailability());
        if (res?.success) {
            setAvailability(res.data.map(el => ({
                id: el._id || el.id,
                start: new Date(el.from),
                end: new Date(el.to),
                isBooked: el.isBooked
            })));
        }
    }, [dispatch]);

    useEffect(() => {
        dispatch(getTeacherData()).then(res => setTeacherData(res));
        fetchAvailability();
    }, [dispatch, fetchAvailability]);

    const addTeacherAvail = async (slots) => {
        return await dispatch(addTeacherAvailability(slots));
    };

    const handleCellClick = (cellStart) => {
        if (cellStart < new Date()) return;

        const existing = availability.find(s => Math.abs(s.start - cellStart) < 1000);
        if (existing) {
            if (existing.isBooked) return;
            setSelectedSlot({ id: existing.id, from: existing.start, to: existing.end });
            setEditEventModal(true);
        } else {
            const slot = {
                from: cellStart,
                to: moment(cellStart).add(duration, 'minutes').toDate(),
                sessionDate: cellStart,
                teacherId: teacherData?.teacherId
            };
            setPendingSlots([slot]);
            setAddEventModal(true);
        }
    };

    const dateRange = useMemo(() => {
        if (viewMode === "today") {
            return [startOfDay(baseDate)];
        } else if (viewMode === "week") {
            const start = startOfWeek(baseDate, { weekStartsOn: 0 });
            return Array.from({ length: 7 }).map((_, i) => addDays(start, i));
        } else {
            const monthStart = startOfMonth(baseDate);
            const monthEnd = endOfMonth(baseDate);
            let current = monthStart;
            const dates = [];
            while (current <= monthEnd) {
                dates.push(current);
                current = addDays(current, 1);
            }
            return dates;
        }
    }, [viewMode, baseDate]);

    const selectedDateSlots = useMemo(() => {
        const fullSlots = [];
        const coveredTimes = new Set();

        TIME_SLOTS.forEach(ts => {
            const [h, m] = ts.id.split(":").map(Number);
            const cellStart = new Date(selectedDate);
            cellStart.setHours(h, m, 0, 0);

            if (coveredTimes.has(cellStart.getTime())) return;

            const slot = availability.find(s => Math.abs(s.start - cellStart) < 1000);
            if (slot) {
                const durationMins = moment(slot.end).diff(moment(slot.start), 'minutes');
                const numBlocks = Math.ceil(durationMins / 30);
                for (let i = 1; i < numBlocks; i++) {
                    coveredTimes.add(moment(cellStart).add(i * 30, 'minutes').toDate().getTime());
                }
            }
            fullSlots.push({ timeSlot: ts, cellStart, slot });
        });
        return fullSlots;
    }, [availability, selectedDate]);

    const getAvailabilityForDate = useCallback((date) => {
        return availability.filter(slot => isSameDay(slot.start, date));
    }, [availability]);

    const navigateCalendar = (direction) => {
        if (viewMode === "today") {
            const newDate = direction > 0 ? addDays(baseDate, 1) : subDays(baseDate, 1);
            setBaseDate(newDate);
            setSelectedDate(newDate);
        } else if (viewMode === "week") {
            setBaseDate(prev => direction > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1));
        } else if (viewMode === "month") {
            setBaseDate(prev => direction > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
        }
    };

    return (
        <div className={styles.matrixContainer}>
            <div style={{ marginBottom: "15px", fontWeight: "800", fontSize: "1.1rem", color: "#dc3545" }}>AVAILABILITY MANAGEMENT</div>
            <div className={styles.toolbar}>
                <div className={styles.dateDisplay}>
                    {viewMode === "today" && format(baseDate, "EEEE, MMMM dd, yyyy")}
                    {viewMode === "week" && `Week of ${format(startOfWeek(baseDate), "MMM dd")} - ${format(addDays(startOfWeek(baseDate), 6), "MMM dd, yyyy")}`}
                    {viewMode === "month" && format(baseDate, "MMMM yyyy")}
                </div>
                <div className={styles.buttonGroup}>
                    <button className={`${styles.navButton} ${viewMode === "today" ? styles.active : ""}`} onClick={() => { setViewMode("today"); setBaseDate(new Date()); setSelectedDate(new Date()); }}>Today</button>
                    <button className={`${styles.navButton} ${viewMode === "week" ? styles.active : ""}`} onClick={() => { setViewMode("week"); setBaseDate(new Date()); }}>Week</button>
                    <button className={`${styles.navButton} ${viewMode === "month" ? styles.active : ""}`} onClick={() => { setViewMode("month"); setBaseDate(new Date()); }}>Month</button>

                    <div style={{ display: "flex", gap: "5px", marginLeft: "5px" }}>
                        <button className={styles.navButton} onClick={() => navigateCalendar(-1)}><i className="fas fa-chevron-left"></i></button>
                        <button className={styles.navButton} onClick={() => navigateCalendar(1)}><i className="fas fa-chevron-right"></i></button>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "10px", borderLeft: "1px solid #eee", paddingLeft: "15px" }}>
                        <span style={{ fontSize: "0.8rem", fontWeight: "bold", color: "#888" }}>DURATION:</span>
                        <div className={styles.durationToggle}>
                            <button className={`${styles.durationBtn} ${duration === 30 ? styles.active : ""}`} onClick={() => setDuration(30)}>30M</button>
                            <button className={`${styles.durationBtn} ${duration === 60 ? styles.active : ""}`} onClick={() => setDuration(60)}>60M</button>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "10px", borderLeft: "1px solid #eee", paddingLeft: "15px" }}>
                        <input className={styles.customDateInput} type="date" value={format(baseDate, "yyyy-MM-dd")} onChange={(e) => { const d = new Date(e.target.value); setBaseDate(d); setSelectedDate(d); }} />
                    </div>
                </div>
            </div>

            {(viewMode === "week" || viewMode === "month") && (
                <div className={styles.calendarGrid}>
                    {WEEKDAYS.map(day => <div key={day} className={styles.dayHeader}>{day}</div>)}
                    {dateRange.map((date, idx) => {
                        const dayAvail = getAvailabilityForDate(date);
                        const isSelected = isSameDay(date, selectedDate);
                        const isToday = isSameDay(date, new Date());
                        const isPast = isBefore(startOfDay(date), startOfDay(new Date()));
                        const isCurrentMonth = viewMode === "month" ? date.getMonth() === baseDate.getMonth() : true;

                        return (
                            <div
                                key={idx}
                                className={`${styles.dayCell} ${isSelected ? styles.isSelected : ""} ${isToday ? styles.isToday : ""} ${dayAvail.length > 0 ? styles.hasAvailability : ""} ${isPast ? styles.disabled : ""}`}
                                style={{ opacity: isCurrentMonth ? (isPast ? 0.3 : 1) : 0.2 }}
                                onClick={() => !isPast && isCurrentMonth && setSelectedDate(date)}
                            >
                                <span className={styles.dayNumber}>{format(date, "d")}</span>
                                {dayAvail.length > 0 && <div className={styles.availabilityDot} />}
                            </div>
                        );
                    })}
                </div>
            )}

            <div>
                <div className={styles.selectedDateHeader}>
                    <span>{format(selectedDate, "EEEE, MMMM dd, yyyy")}</span>
                    <span>{getAvailabilityForDate(selectedDate).length} slots set</span>
                </div>
                <div className={styles.timeSlotGrid} onMouseLeave={() => setHoveredIdx(null)}>
                    {selectedDateSlots.map(({ timeSlot, cellStart, slot }, idx) => {
                        const isPast = isBefore(cellStart, new Date());
                        const isPrimaryHover = hoveredIdx === idx;
                        const isSecondaryHover = duration === 60 && hoveredIdx !== null && idx === hoveredIdx + 1;
                        const isHovered = isPrimaryHover || isSecondaryHover;

                        return (
                            <button
                                key={timeSlot.id}
                                className={`${styles.timeSlotButton} ${slot ? (slot.isBooked ? styles.isBooked : styles.exists) : ""} ${isPast ? styles.disabled : ""} ${isHovered ? styles.isHovered : ""}`}
                                disabled={isPast}
                                onMouseEnter={() => !isPast && setHoveredIdx(idx)}
                                onClick={() => !isPast && handleCellClick(cellStart)}
                            >
                                {timeSlot.label}
                                {slot ? (slot.isBooked ? " (B)" : " (A)") : " +"}
                            </button>
                        );
                    })}
                </div>
            </div>

            {addEventModal &&
                <AddEventModal
                    setAddEventModal={setAddEventModal}
                    slots={pendingSlots}
                    addSlots={addTeacherAvail}
                    getAvailability={fetchAvailability}
                />
            }
            {editEventModal &&
                <EditEventModal
                    setEditEventModal={setEditEventModal}
                    selectedSlot={selectedSlot}
                    availability={availability}
                    setAvailability={setAvailability}
                />
            }
        </div>
    );
};

export default AvailabilityGrid;
